import React, { useState, useEffect } from 'react';
import { Card, Tabs, Input, Select, List, Typography, Tag, Space } from 'antd';
import { SearchOutlined, WarningOutlined, InfoCircleOutlined, CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styles from './Log.module.scss';

const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const LogViewer = ({ messages, dmesg, syslog, bootlog }) => {
  const [activeTab, setActiveTab] = useState('messages');
  const [searchText, setSearchText] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [logLevel, setLogLevel] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // 로그 데이터 파싱
  const parseLogData = (data) => {
    if (!data || data === 'N/A') return [];
    
    try {
      const logs = [];
      const lines = data.trim().split('\n');
      
      lines.forEach((line, index) => {
        // 로그 레벨 추출 (간단한 휴리스틱)
        let level = 'info';
        if (line.toLowerCase().includes('error') || line.toLowerCase().includes('fail')) {
          level = 'error';
        } else if (line.toLowerCase().includes('warn')) {
          level = 'warning';
        } else if (line.toLowerCase().includes('info')) {
          level = 'info';
        } else if (line.toLowerCase().includes('debug')) {
          level = 'debug';
        }
        
        logs.push({
          id: index,
          content: line,
          level,
          key: `log-${index}`
        });
      });
      
      return logs;
    } catch (error) {
      console.error('Error parsing log data:', error);
      return [];
    }
  };

  const getActiveLogData = () => {
    switch (activeTab) {
      case 'messages':
        return parseLogData(messages);
      case 'dmesg':
        return parseLogData(dmesg);
      case 'syslog':
        return parseLogData(syslog);
      case 'bootlog':
        return parseLogData(bootlog);
      default:
        return [];
    }
  };

  useEffect(() => {
    const logData = getActiveLogData();
    filterLogs(logData, searchText, logLevel);
    setCurrentPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchText, logLevel]);

  const filterLogs = (logs, search, level) => {
    let filtered = logs;
    
    if (search) {
      filtered = filtered.filter(log => 
        log.content.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    if (level !== 'all') {
      filtered = filtered.filter(log => log.level === level);
    }
    
    setFilteredLogs(filtered);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleLevelChange = (value) => {
    setLogLevel(value);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchText('');
    setLogLevel('all');
    setCurrentPage(1);
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'info':
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      case 'debug':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <Card title="로그 분석" className={styles.logCard}>
      <div className={styles.logControls}>
        <Space>
          <Search
            placeholder="로그 검색..."
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Select
            defaultValue="all"
            style={{ width: 120 }}
            onChange={handleLevelChange}
            value={logLevel}
          >
            <Option value="all">모든 레벨</Option>
            <Option value="error">에러</Option>
            <Option value="warning">경고</Option>
            <Option value="info">정보</Option>
            <Option value="debug">디버그</Option>
          </Select>
        </Space>
        <div className={styles.logStats}>
          <Tag color="blue">{filteredLogs.length} 로그 항목</Tag>
        </div>
      </div>
      
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="messages" key="messages">
          <List
            size="small"
            bordered
            dataSource={paginatedLogs}
            pagination={{
              onChange: page => setCurrentPage(page),
              pageSize,
              current: currentPage,
              total: filteredLogs.length,
              showSizeChanger: false
            }}
            renderItem={item => (
              <List.Item>
                <Space>
                  {getLevelIcon(item.level)}
                  <Text
                    className={
                      item.level === 'error' ? styles.errorText :
                      item.level === 'warning' ? styles.warningText :
                      item.level === 'debug' ? styles.debugText :
                      styles.infoText
                    }
                  >
                    {item.content}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        </TabPane>
        <TabPane tab="dmesg" key="dmesg">
          <List
            size="small"
            bordered
            dataSource={paginatedLogs}
            pagination={{
              onChange: page => setCurrentPage(page),
              pageSize,
              current: currentPage,
              total: filteredLogs.length,
              showSizeChanger: false
            }}
            renderItem={item => (
              <List.Item>
                <Space>
                  {getLevelIcon(item.level)}
                  <Text
                    className={
                      item.level === 'error' ? styles.errorText :
                      item.level === 'warning' ? styles.warningText :
                      item.level === 'debug' ? styles.debugText :
                      styles.infoText
                    }
                  >
                    {item.content}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        </TabPane>
        <TabPane tab="syslog" key="syslog">
          <List
            size="small"
            bordered
            dataSource={paginatedLogs}
            pagination={{
              onChange: page => setCurrentPage(page),
              pageSize,
              current: currentPage,
              total: filteredLogs.length,
              showSizeChanger: false
            }}
            renderItem={item => (
              <List.Item>
                <Space>
                  {getLevelIcon(item.level)}
                  <Text
                    className={
                      item.level === 'error' ? styles.errorText :
                      item.level === 'warning' ? styles.warningText :
                      item.level === 'debug' ? styles.debugText :
                      styles.infoText
                    }
                  >
                    {item.content}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        </TabPane>
        <TabPane tab="boot.log" key="bootlog">
          <List
            size="small"
            bordered
            dataSource={paginatedLogs}
            pagination={{
              onChange: page => setCurrentPage(page),
              pageSize,
              current: currentPage,
              total: filteredLogs.length,
              showSizeChanger: false
            }}
            renderItem={item => (
              <List.Item>
                <Space>
                  {getLevelIcon(item.level)}
                  <Text
                    className={
                      item.level === 'error' ? styles.errorText :
                      item.level === 'warning' ? styles.warningText :
                      item.level === 'debug' ? styles.debugText :
                      styles.infoText
                    }
                  >
                    {item.content}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default LogViewer;